import { ReactNode, useState } from 'react'
import { useTranslation } from '@mc/i18n'
import { TRACK_CONTENT_TYPES } from '@mc/track-event'
import { useIsEnterpriseMember, useModalLock } from 'pages/certificates/common'
import {
  TrailerComingSoonPauseScreen,
  TrailerEnrolledPauseScreen,
  TrailerPauseScreen,
  VideoModal,
} from 'pages/certificates/components'
import { CertHeadlineHero } from 'pages/certificates/components/MarketingCertPage/HeroSection/CertHeadlineHero'
import { Mccerts2279HeroTrailerLoopExp } from 'pages/certificates/experiments/mccerts-2279-trailer-thumbnail-autoplay'
import { TrackingAttributes, useFireEvent } from 'pages/certificates/tracking'
import { HeroLayout } from './HeroLayout'
import { type HeroVariantProps } from './types'

// Based on components/MarketingCertPage/HeroSection/HeroSection.tsx.
//
// Reusable CM hero shell for the experiment variants: renders the shared hero
// (headline, trailer) and slots each variant's treatment in via `children`.
const TITLE_ID = 'certificate.title'

type HeroVariantSharedProps = HeroVariantProps & {
  children?: ReactNode
  // Opt-in per arm: only the selector variants pass this, because they drop the
  // EngagementPromo box that otherwise carries the release date. variant_3 keeps
  // that box, so passing it there would show the date twice.
  releaseLabel?: string
}

export const HeroVariant = ({
  program,
  comingSoon = false,
  enrollment,
  certUuid,
  certSlug,
  children,
  releaseLabel,
}: HeroVariantSharedProps) => {
  const { t } = useTranslation('@mc/certificates')
  const fireEvent = useFireEvent()
  const [showTrailerVideo, setShowTrailerVideo] = useState(false)
  const isEnterprise = useIsEnterpriseMember()
  const { acquireLock: acquireModalLock, releaseLock: releaseModalLock } =
    useModalLock()

  const { trailerMediaUuid } = program.videos || {}
  const logo = program.partner?.images?.logo

  const getPauseScreen = () => {
    const isUserEnrolled = enrollment?.isEnrolled
    const showComingSoon = comingSoon && isUserEnrolled
    const showEnrolled = !comingSoon && isUserEnrolled
    const handleVideoClose = () => {
      setShowTrailerVideo(false)
      releaseModalLock()
    }

    if (showComingSoon)
      return <TrailerComingSoonPauseScreen onClickBack={handleVideoClose} />

    if (showEnrolled)
      return <TrailerEnrolledPauseScreen onClickBack={handleVideoClose} />

    return (
      <TrailerPauseScreen
        subtext={program.certificateShowcase?.subtext}
        showCTA={!isEnterprise}
      />
    )
  }

  const onTrailerClick = () => {
    setShowTrailerVideo(true)
    acquireModalLock()
    fireEvent('certificateLandingClick', {
      action: t('marketingPages.common.playTrailer'),
    })
  }

  return (
    <>
      <section aria-labelledby={TITLE_ID}>
        <HeroLayout
          imageUrl={program.images?.hero1x1?.url}
          imageAlt={program.images?.hero1x1?.altText || ''}
          showTrailerButton={!!trailerMediaUuid}
          onTrailerClick={onTrailerClick}
          trailerMediaUuid={trailerMediaUuid}
          // mccerts-2279 runs independently of this experiment's bucket, so the
          // trailer loop is wired here as well as in HeroSection — otherwise it
          // would only ever reach visitors in this experiment's control arm.
          media={
            !!trailerMediaUuid && (
              <Mccerts2279HeroTrailerLoopExp
                mediaUuid={trailerMediaUuid}
                posterUrl={program.images?.hero1x1?.url}
                paused={showTrailerVideo}
                certSlug={certSlug}
              />
            )
          }
        >
          <CertHeadlineHero
            heroSection={program.heroSection}
            programTitle={program.title}
            programSubtitle={program.subtitle}
            companyLogo={logo}
            releaseLabel={releaseLabel}
          />

          {children}
        </HeroLayout>
      </section>
      {trailerMediaUuid && (
        <TrackingAttributes location='certificate trailer end screen'>
          <VideoModal
            show={showTrailerVideo}
            onCloseModal={() => {
              setShowTrailerVideo(false)
              releaseModalLock()
            }}
            mediaUuid={trailerMediaUuid}
            contentId={certUuid}
            contentType={TRACK_CONTENT_TYPES.CERTIFICATE_TRAILER}
            contentTitle={program.title ?? ''}
            pauseScreen={getPauseScreen()}
          />
        </TrackingAttributes>
      )}
    </>
  )
}
